/**
 * FlowAI Email-Config — globale SMTP-Konfiguration für FlowAI-Outbound.
 *
 * @page /flowai/admin/email
 * @since v2.486.2
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, Card, Button } from '../../components/ui';
import FlowAIBreadcrumb from '../../components/flowai/FlowAIBreadcrumb';

const API = import.meta.env.VITE_API_URL || '';

export default function FlowAIEmailConfig() {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState({
    flowai_smtp_host: '',
    flowai_smtp_port: 465,
    flowai_smtp_user: '',
    flowai_smtp_from_email: '',
    flowai_smtp_from_name: '',
    flowai_smtp_tls: true,
  });
  const [password, setPassword] = useState('');
  const [passwordSet, setPasswordSet] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saveMsg, setSaveMsg] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/flowai/email-config`, { credentials: 'include' });
      const d = await r.json().catch(() => ({}));
      if (d?.config) {
        setCfg({
          flowai_smtp_host: d.config.flowai_smtp_host || '',
          flowai_smtp_port: Number(d.config.flowai_smtp_port) || 465,
          flowai_smtp_user: d.config.flowai_smtp_user || '',
          flowai_smtp_from_email: d.config.flowai_smtp_from_email || '',
          flowai_smtp_from_name: d.config.flowai_smtp_from_name || '',
          flowai_smtp_tls: d.config.flowai_smtp_tls === true || d.config.flowai_smtp_tls === 'true',
        });
      }
      setPasswordSet(!!d?.passwordSet);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    setSaving(true);
    setSaveMsg(null);
    try {
      const body = { ...cfg };
      if (password.length > 0) body.flowai_smtp_password = password;
      const r = await fetch(`${API}/api/flowai/email-config`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.ok) {
        setSaveMsg({ type: 'ok', text: 'Gespeichert.' });
        setPassword('');
        await load();
      } else {
        setSaveMsg({ type: 'err', text: d.error || 'Fehler beim Speichern' });
      }
    } catch (err) {
      setSaveMsg({ type: 'err', text: err.message });
    } finally {
      setSaving(false);
    }
  }

  async function test() {
    if (!testTo) { setTestResult({ ok: false, error: 'Empfänger-Adresse fehlt' }); return; }
    setTesting(true);
    setTestResult(null);
    try {
      const r = await fetch(`${API}/api/flowai/email-config/test`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: testTo }),
      });
      const d = await r.json().catch(() => ({}));
      setTestResult(d);
    } catch (err) {
      setTestResult({ ok: false, error: err.message });
    } finally {
      setTesting(false);
    }
  }

  if (loading) return <div className="p-6 text-sm">Lade Konfiguration…</div>;

  return (
    <div className="p-6 max-w-3xl">
      <FlowAIBreadcrumb trail={[{ label: 'FlowAI', to: '/flowai' }, { label: 'Admin' }, { label: 'Email-Konfiguration' }]} />
      <PageHeader
        title="FlowAI SMTP-Konfiguration"
        subtitle="Globaler Outbound-SMTP-Account für B2B-Notifications (Workspace-übergreifend, #1086)."
        actions={<Button variant="ghost" onClick={() => navigate('/flowai')}>← Cockpit</Button>}
      />

      <Card title="SMTP-Account">
        <Row label="Host">
          <input
            type="text"
            value={cfg.flowai_smtp_host}
            onChange={(e) => setCfg({ ...cfg, flowai_smtp_host: e.target.value })}
            placeholder="m.zsuite.at"
            className="w-full px-3 py-1.5 border border-[var(--border-subtle,#E5E7EB)] rounded text-sm"
          />
        </Row>
        <Row label="Port">
          <input
            type="number"
            value={cfg.flowai_smtp_port}
            onChange={(e) => setCfg({ ...cfg, flowai_smtp_port: Number(e.target.value) || 0 })}
            placeholder="465"
            className="w-full px-3 py-1.5 border border-[var(--border-subtle,#E5E7EB)] rounded text-sm"
          />
        </Row>
        <Row label="TLS / SSL">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={cfg.flowai_smtp_tls}
              onChange={(e) => setCfg({ ...cfg, flowai_smtp_tls: e.target.checked })}
            />
            <span>Verschlüsselung aktiv (Port 465 → an, Port 587/STARTTLS → meist aus)</span>
          </label>
        </Row>
        <Row label="User">
          <input
            type="text"
            value={cfg.flowai_smtp_user}
            onChange={(e) => setCfg({ ...cfg, flowai_smtp_user: e.target.value })}
            placeholder="flowai@email.weship.eu"
            autoComplete="off"
            className="w-full px-3 py-1.5 border border-[var(--border-subtle,#E5E7EB)] rounded text-sm"
          />
        </Row>
        <Row label="Passwort">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={passwordSet ? '•••••••• (leer lassen = unverändert)' : 'Passwort eingeben'}
            autoComplete="new-password"
            className="w-full px-3 py-1.5 border border-[var(--border-subtle,#E5E7EB)] rounded text-sm"
          />
          {passwordSet && password.length === 0 && (
            <div className="text-[11px] text-[var(--text-tertiary,#6B7280)] mt-1">Passwort gespeichert. Leeres Feld = unverändert.</div>
          )}
        </Row>
      </Card>

      <Card title="Absender">
        <Row label="From-Email">
          <input
            type="email"
            value={cfg.flowai_smtp_from_email}
            onChange={(e) => setCfg({ ...cfg, flowai_smtp_from_email: e.target.value })}
            placeholder="flowai@email.weship.eu"
            className="w-full px-3 py-1.5 border border-[var(--border-subtle,#E5E7EB)] rounded text-sm"
          />
        </Row>
        <Row label="From-Name">
          <input
            type="text"
            value={cfg.flowai_smtp_from_name}
            onChange={(e) => setCfg({ ...cfg, flowai_smtp_from_name: e.target.value })}
            placeholder="FlowAI · WeShip"
            className="w-full px-3 py-1.5 border border-[var(--border-subtle,#E5E7EB)] rounded text-sm"
          />
        </Row>
      </Card>

      <div className="flex items-center gap-3 mb-6">
        <Button onClick={save} disabled={saving}>{saving ? 'Speichere…' : 'Speichern'}</Button>
        {saveMsg && (
          <span className={`text-sm ${saveMsg.type === 'ok' ? 'text-green-700' : 'text-red-700'}`}>{saveMsg.text}</span>
        )}
      </div>

      <Card title="Verbindung testen">
        <div className="text-[12px] text-[var(--text-tertiary,#6B7280)] mb-3">
          Sendet eine kurze Test-Mail über die FlowAI-SMTP-Konfiguration. Letzte gespeicherten Werte werden verwendet — wenn du gerade Felder geändert hast, erst <strong>Speichern</strong> klicken.
        </div>
        <div className="flex items-center gap-2">
          <input
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="empfaenger@example.com"
            className="flex-1 px-3 py-1.5 border border-[var(--border-subtle,#E5E7EB)] rounded text-sm"
          />
          <Button variant="secondary" onClick={test} disabled={testing}>{testing ? 'Sende…' : 'Test senden'}</Button>
        </div>
        {testResult && (
          <div className={`mt-3 p-2 rounded text-[12px] ${testResult.ok ? 'bg-green-50 border border-green-300 text-green-900' : 'bg-red-50 border border-red-300 text-red-900'}`}>
            {testResult.ok ? (
              <>✓ Versendet. <code>messageId={testResult.messageId}</code></>
            ) : (
              <>✗ Fehler: {testResult.error || 'unbekannt'}</>
            )}
          </div>
        )}
      </Card>

      <div className="text-[11px] text-[var(--text-tertiary,#6B7280)] mt-4 leading-snug">
        Werte werden in <code>system_config</code> unter den Keys <code>flowai_smtp_host/port/user/password/from_email/from_name/tls</code> gespeichert (Kategorie <code>flowai</code>).
        Wird vom Skill <code>send_b2b_notification</code> via <code>connection-bridge.sendMail({'{'} connectionKey: 'flowai_smtp' {'}'})</code> gelesen.
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="py-1.5">
      <div className="text-[11px] uppercase text-[var(--text-tertiary,#6B7280)] mb-1">{label}</div>
      {children}
    </div>
  );
}
